import { CurrencyPipe, LowerCasePipe } from '@angular/common';
import { Component, computed, Injector, OnInit, Pipe, PipeTransform, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { filter, switchMap } from 'rxjs';
import { DialogManager } from '../../../../../../../global/dialog-manager/dialog';
import { OrbitUtils } from '../../../../../../../global/orbit/orbit-utils';
import { AddressLookupComponent } from '../../../../../../../shared/components/address-lookup/address-lookup.component';
import { ButtonComponent } from '../../../../../../../shared/components/button/button.component';
import { CheckboxComponent } from '../../../../../../../shared/components/forms/checkbox/checkbox.component';
import { LoadingSpinnerComponent } from '../../../../../../../shared/components/forms/loading-spinner/loading-spinner.component';
import { UnavailableDocumentsComponent } from '../../../../../../../shared/components/unavailable-documents/unavailable-documents.component';
import { AnimateOpenDirective } from '../../../../../../../shared/directives/animate-open.directive';
import { QMBlockDirective } from '../../../../../../../shared/directives/quantum-metric.directive';
import { PlanDocumentKey } from '../../../../../../../shared/interfaces/plan-docs-interfaces';
import { PlanDocumentsService } from '../../../../../../../shared/services/plan-document.service';
import { addressFormatting } from '../../../../../../../shared/utils/formatting';
import { OrbitFormClaimService } from '../../../../services/orbit-form-claim.service';
import { PaymentService } from './services/payment-service.service';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
declare const WPCL;

export enum WorldPayPaymentStatus {
  Authorised = 'AUTHORISED',
  Refused = 'REFUSED',
  Error = 'ERROR',
  Hold_Card = 'REFUSED4',
  Refused5 = 'REFUSED5',
  Approve_After_Identification = 'REFUSED8',
  Invalid_Amount = 'REFUSED13',
  Invalid_Card_Issuer = 'REFUSED15',
  Annulation_By_Client = 'REFUSED17',
  Access_Denied = 'REFUSED28',
  Impossible_Reference_Number = 'REFUSED29',
  Card_Expired = 'REFUSED33',
  Fraud_Suspicion = 'REFUSED34',
  Security_Code_Expired = 'REFUSED38',
  Lost_Card = 'REFUSED41',
  Stolen_Card_Pick_Up = 'REFUSED43',
  Limit_Exceeded = 'REFUSED51',
  Invalid_Security_Code = 'REFUSED55',
  Unknown_Card = 'REFUSED56',
  Illegal_Transaction = 'REFUSED57',
  Restricted_Card = 'REFUSED62',
  Security_Rules_Voilated = 'REFUSED63',
  Security_Code_Invalid = 'REFUSED75',
  Card_Blocked = 'REFUSED76',
  Rejected_By_Card_Issuer = 'REFUSED85',
  Revocation_Of_Authorization_Order = 'REFUSEDR1',
  Revocation_Of_All_Authorizations_Order = 'REFUSEDR2',
}

export interface WorldPayResponse {
  order: {
    orderKey?: string;
    status?: WorldPayOrderStatus;
  };
  error?: string;
  gateway: {
    orderKey?: string;
    mac2?: string;
    paymentCurrency?: string;
    paymentAmount?: string;
    paymentStatus?: WorldPayPaymentStatus;
    mac?: string;
  };
}

export enum WorldPayOrderStatus {
  Success = 'success',
  Cancelled = 'cancelled_by_shopper',
  AlreadyProcessed = 'already_processed',
}

@Pipe({
  name: 'customRound',
  standalone: true,
})
export class CustomRoundPipe implements PipeTransform {
  transform(value: string | null): unknown {
    if (!value) return '';

    return `${value}`.replace('.00', '');
  }
}
@Component({
  selector: 'app-payment-form',
  imports: [
    CurrencyPipe,
    CustomRoundPipe,
    AnimateOpenDirective,
    LowerCasePipe,
    CheckboxComponent,
    QMBlockDirective,
    FormsModule,
    ButtonComponent,
    AddressLookupComponent,
    LoadingSpinnerComponent,
  ],
  templateUrl: './payment-form.component.html',
  styleUrl: './payment-form.component.scss',
})
export class PaymentFormComponent implements OnInit {
  panelExpanded = signal(false);
  addressPanelExpanded = signal(false);
  isDownloadingDocs = signal(false);

  useHomeBillingAddress = true;
  billingAddressValue: Record<string, string> | undefined;
  billingAddressFormError = false;
  initialPaymentLoad = true;

  autoSearchAddress = '';
  useBillingAddress = signal(false);

  private viewModel = computed(() => {
    const isCallout = this.orbitFormClaimService.state.latestStep?.paymentData?.[0]?.type === 'CALLOUT';

    const homeAddress = [
      this.orbitFormClaimService.state.claimData?.bundle?.addressLine1,
      this.orbitFormClaimService.state.claimData?.bundle?.addressLine2,
      this.orbitFormClaimService.state.claimData?.bundle?.addressLine3,
      this.orbitFormClaimService.state.claimData?.bundle?.addressLine4,
      this.orbitFormClaimService.state.claimData?.bundle?.postalCode,
    ];

    return {
      claimId: this.orbitFormClaimService.claimID || this.orbitFormClaimService.state.claimData.bundle.claimId,
      paymentCost: parseFloat(this.orbitFormClaimService.state.latestStep?.paymentData?.[0]?.amount ?? '0.00'),
      paymentType: isCallout ? 'call-out fee' : 'excess payment',
      submittingResource: this.orbitFormClaimService.formSubmission,
      panelExpanded: this.panelExpanded(),
      addressPanelExpanded: this.addressPanelExpanded(),
      paymentPanelDescription: isCallout
        ? [
            'A call-out fee is the amount you pay towards the overall cost of a repair. This was agreed when you purchased your plan.',
          ]
        : [
            'In insurance, ‘excess’ means an amount the customer has to pay towards the overall cost of a claim. This amount is agreed when the customer purchases the policy.',
          ],
      useBillingAddress: this.useBillingAddress(),
      billingAddress: this.useBillingAddress()
        ? addressFormatting(Object.values(this.billingAddressValue ?? {}), true)
        : undefined,
      homeAddress: addressFormatting(homeAddress, true),
      addressArray: this.useBillingAddress()
        ? addressFormatting(Object.values(this.billingAddressValue ?? {}))
        : addressFormatting(homeAddress),
      planNumber: this.orbitFormClaimService.state.claimData?.bundle?.planNumber,
      worldPayDataLoading: this.paymentService.worldPayRequest.isLoading(),
      worldPayDataError: this.paymentService.worldPayRequest.error(),
      putServiceOptionSubmitting:
        this.paymentService.putPaymentRequest.isLoading() || this.orbitFormClaimService.formSubmission.isLoading(),
      putServiceOptionError:
        this.paymentService.putPaymentRequest.error() || this.orbitFormClaimService.formSubmission.error(),
      claimType: this.orbitFormClaimService.state.claimData?.bundle?.claimType?.toLowerCase(),
      isHomeRepair:
        this.orbitFormClaimService.storedValue['service-options']?.option?.serviceOptionType === 'Home Repair',
    };
  });
  page: string | undefined;

  customOptions:
    | {
        type: string;
        inject: string;
        iframeIntegrationId: string;
        flowCallback: (flow: any) => void;
        resultCallback: (result: any) => void;
        iframeHelperURL: string;
        iframeBaseURL: string;
        url: string;
        target: string;
        accessibility: boolean;
        debug: boolean;
        language: string;
        country: string;
        cancelURL: string;
        customisation: {
          fonts: { family: string[] };
          // color: this.getCssVar('text-colour'),
          buttons: {
            // backgroundColor: this.getCssVar('btn-primary-background-color'),
            // color: this.getCssVar('btn-primary-color'),
            padding: string;
            height: string;
            // border: {
            //   style: 'solid',
            //   color: this.getCssVar('btn-primary-border-color'),
            //   //width: '5px', //this.getCssVar('btn-border-width') WL TODO: coming out as 1.5px for some reason which doesn't work with worldpay
            //   // radius: this.getCssVar('btn-border-radius'),
            // },
            hover: {
              // backgroundColor: this.getCssVar('primary-interactive-color--hover'),
              border: {};
            };
          };
          inputs: {
            // backgroundColor: this.getCssVar('input-background-color'),
            border: { style: string };
            placeholder: {};
            validation: { ok: {}; error: {} };
          };
          page: {
            // backgroundColor: this.getCssVar('page-bg'),
            // color: this.getCssVar('text-colour'),
            border: { style: string };
          };
        };
        errorURL: string;
        expiryURL: string;
        failureURL: string;
      }
    | undefined;
  libraryObject:
    | {
        setup: (arg0: {
          type: string;
          inject: string;
          iframeIntegrationId: string;
          flowCallback: (flow: any) => void;
          resultCallback: (result: any) => void;
          iframeHelperURL: string;
          iframeBaseURL: string;
          url: string;
          target: string;
          accessibility: boolean;
          debug: boolean;
          language: string;
          country: string;
          cancelURL: string;
          customisation: {
            fonts: { family: string[] };
            // color: this.getCssVar('text-colour'),
            buttons: {
              // backgroundColor: this.getCssVar('btn-primary-background-color'),
              // color: this.getCssVar('btn-primary-color'),
              padding: string;
              height: string;
              // border: {
              //   style: 'solid',
              //   color: this.getCssVar('btn-primary-border-color'),
              //   //width: '5px', //this.getCssVar('btn-border-width') WL TODO: coming out as 1.5px for some reason which doesn't work with worldpay
              //   // radius: this.getCssVar('btn-border-radius'),
              // },
              hover: {
                // backgroundColor: this.getCssVar('primary-interactive-color--hover'),
                border: {};
              };
            };
            inputs: {
              // backgroundColor: this.getCssVar('input-background-color'),
              border: { style: string };
              placeholder: {};
              validation: { ok: {}; error: {} };
            };
            page: {
              // backgroundColor: this.getCssVar('page-bg'),
              // color: this.getCssVar('text-colour'),
              border: { style: string };
            };
          };
          errorURL: string;
          expiryURL: string;
          failureURL: string;
        }) => void;
      }
    | undefined;

  result: WorldPayResponse | undefined;

  customisation = {
    fonts: {
      family: ['Arial'],
    },
    // color: this.getCssVar('text-colour'),
    buttons: {
      // backgroundColor: this.getCssVar('btn-primary-background-color'),
      // color: this.getCssVar('btn-primary-color'),
      padding: '40px',
      height: '50px',
      border: {
        style: 'solid',
        //   color: this.getCssVar('btn-primary-border-color'),
        width: '1px', //this.getCssVar('btn-border-width') WL TODO: coming out as 1.5px for some reason which doesn't work with worldpay
        radius: '8px',
      },
      hover: {
        // backgroundColor: this.getCssVar('primary-interactive-color--hover'),
        border: {
          // color: this.getCssVar('primary-interactive-color--hover'),
        },
      },
    },
    inputs: {
      // backgroundColor: this.getCssVar('input-background-color'),
      border: {
        style: 'solid',
        // width: this.getCssVar('input-border-width'),
        // radius: this.getCssVar('input-border-radius'),
        // color: this.getCssVar('input-border-color'),
      },
      placeholder: {
        // color: this.getCssVar('text-color--placeholder'),
      },
      validation: {
        ok: {
          // color: this.getCssVar('success'),
        },
        error: {
          // color: this.getCssVar('error'),
        },
      },
    },
    page: {
      // backgroundColor: this.getCssVar('page-bg'),
      // color: this.getCssVar('text-colour'),
      border: {
        style: 'none',
        width: '0px',
        // color: this.getCssVar('input-border-color'),
        // width: this.getCssVar('input-border-width'),
        // radius: this.getCssVar('input-border-radius'),
      },
    },
  };

  orderNumber: string | undefined;

  get vm() {
    return this.viewModel();
  }

  constructor(
    private readonly injector: Injector,
    private readonly orbitFormClaimService: OrbitFormClaimService,
    private readonly paymentService: PaymentService,
    private readonly planDocsService: PlanDocumentsService
  ) {}

  ngOnInit() {
    this.listenToFormSubmissionFailures();
    this.getWorldpayDataUrl();
  }

  getWorldpayDataUrl() {
    const domain = window.location.origin;

    this.paymentService.getWorldpayDataReference(
      this.orbitFormClaimService.state.claimData,
      this.orbitFormClaimService.storedValue,
      this.vm.claimId!,
      this.vm.paymentCost,
      this.vm.useBillingAddress ? this.billingAddressValue : undefined
    );

    const planNumber = this.orbitFormClaimService.state.claimData.bundle.planNumber;
    toObservable(this.paymentService.worldPayRequest.value, { injector: this.injector })
      .pipe(filter((v) => !!v))
      .subscribe({
        next: (response) => {
          if (this.initialPaymentLoad) {
            this.initialPaymentLoad = false;
          }
          this.orderNumber = response.orderNumber;
          this.customOptions = {
            type: 'iframe',
            inject: 'immediate',
            iframeIntegrationId: 'libraryObject',
            flowCallback: this.flowCallbackFn,
            resultCallback: this.resultCallbackFn,
            iframeHelperURL: `${domain}/worldpay/helper.html`,
            iframeBaseURL: `${domain}`,
            url: `${response.url}`,
            target: 'paymentForm',
            accessibility: true,
            debug: false,
            language: 'en',
            country: 'gb',
            cancelURL: `${domain}/${planNumber}/orbit/payment`,
            customisation: this.customisation,
            errorURL: `${domain}/${planNumber}/orbit/error`,
            expiryURL: `${domain}/${planNumber}/orbit/error`,
            failureURL: `${domain}/${planNumber}/orbit/error`,
          };

          this.libraryObject = new WPCL.Library();

          setTimeout(() => {
            this.libraryObject!.setup(this.customOptions!); // push to end of stack
          });
        },
      });
  }

  listenToFormSubmissionFailures() {
    toObservable(this.orbitFormClaimService.formSubmission.error, { injector: this.injector })
      .pipe(filter((v) => !!v))
      .subscribe({
        next: (error) => {
          OrbitUtils.sendUserToErrorPage(error as any);
        },
      });
  }

  flowCallbackFn = (flow: { page: string }) => {
    this.page = flow.page;
  };

  resultCallbackFn = (result: WorldPayResponse) => {
    this.result = result;
    if (this.result.order.status === WorldPayOrderStatus.Success) {
      this.paymentService.updateClaimWithPayment(this.vm.claimId!, this.orderNumber!, this.vm.paymentCost);

      toObservable(this.paymentService.putPaymentRequest.value, { injector: this.injector })
        .pipe(filter((v) => !!v))
        .subscribe({
          next: () => {
            const paymentType = this.vm.paymentType;
            const isCallout = paymentType === 'call-out fee';
            this.orbitFormClaimService.storeValue('payment', {
              paymentTaken: true,
              amount: this.vm.paymentCost,
              type: isCallout ? 'Call-out' : 'Excess',
              paidLabel: isCallout ? 'call-out fee' : 'excess',
              billingAddress: this.vm.addressArray,
            });

            this.orbitFormClaimService.storeCurrentClaimStateForResuming('payment');
            console.log('Moving step forward from payment');
            this.orbitFormClaimService.moveStepForward('payment');
          },
        });
    } else {
      console.log('Payment not successful:', this.result.order.status);
    }
  };

  downloadDocuments(): void {
    const planNumber = this.vm.planNumber;
    if (!planNumber) return;

    this.isDownloadingDocs.set(true);
    this.planDocsService
      .searchPlanDocumentKey(planNumber)
      .pipe(
        switchMap((response: PlanDocumentKey) => {
          return this.planDocsService.downloadPlanDocument(planNumber, response);
        })
      )
      .subscribe({
        next: () => {
          this.isDownloadingDocs.set(false);
        },
        error: () => {
          this.isDownloadingDocs.set(false);
          this.showFailureToDownloadPlan();
        },
      });
  }

  showFailureToDownloadPlan(): void {
    DialogManager.openDialog(UnavailableDocumentsComponent, {});
  }

  updateBillingAddress(useHomeBilling: boolean): void {
    // TODO: this can be simplified
    if (useHomeBilling) {
      this.billingAddressFormError = false;
      this.billingAddressValue = undefined;
      this.useBillingAddress.set(false);
      this.paymentService.getWorldpayDataReference(
        this.orbitFormClaimService.state.claimData,
        this.orbitFormClaimService.storedValue,
        this.vm.claimId!,
        this.vm.paymentCost,
        undefined
      );
      return;
    }

    this.billingAddressValue = {
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      postalCode: '',
    };
  }

  saveBillingAddress(address: {
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    postalCode: string;
  }): void {
    // if (!this.billingAddressValue!['addressLine1'] || !this.billingAddressValue!['postalCode']) {
    //   this.billingAddressFormError = true;
    //   return;
    // }
    this.billingAddressValue = address;
    this.useBillingAddress.set(true);
    // TODO: hook this up to output

    this.paymentService.getWorldpayDataReference(
      this.orbitFormClaimService.state.claimData,
      this.orbitFormClaimService.storedValue,
      this.vm.claimId!,
      this.vm.paymentCost,
      this.billingAddressValue
    );
  }

  get paymentReason(): string {
    if (this.vm.isHomeRepair) {
      return 'You’re almost there – this payment is the last step before choosing an engineer visit date.';
    }
    return `This payment is required for your ${this.vm.claimType}.`;
  }
}
